import { Text, TextProps } from 'components'
import objectHash from 'object-hash'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import textFit, { TextFitOption } from 'textfit'
import { WithRequired } from 'types'
import { ScaleProps, useScale, useScaleProps } from '../../utils/scaleUtils'
import { useExplorerConfig } from '../providers'

export interface Props extends ScaleProps<Omit<TextProps, 'children' | 'variant'>> {
  children?: string | string[]
  /** (If fitToWidth is set) Scale text to fit to */
  fitToHeight?: number | 'one-line'
  /** Scale text to fit to width */
  fitToWidth?: number | '100%'
  /** (Default 16) Set the size of text to scale (or maximum size if fitToWidth is set) */
  fontSize?: number
  /** (If fitToWidth is set, default 8) Set the maximum fontSize to scale to */
  minFontSize?: number
  fontWeight?: CSSProperties['fontWeight']
  /** Default 1.1 */
  lineHeight?: CSSProperties['lineHeight']
  onFitted?: (props: OnFittedProps) => void
}

interface OnFittedProps {
  /** scaled */
  fontSize: number
  /** scaled */
  height: number
  /** scaled */
  width: number
}

export default function SText({ fitToWidth, lineHeight = 1.1, ...rest }: Props) {
  const { theme } = useExplorerConfig()
  const fontFamily = theme?.fontFamily
  const commonProps = { lineHeight, fontFamily, ...rest }
  return fitToWidth ? (
    <FittedText fitToWidth={fitToWidth} {...commonProps} />
  ) : (
    <ScaledText {...commonProps} />
  )
}

function ScaledText(props: Props) {
  const { styleProps, rest } = useScaleProps(props)
  return <Text {...styleProps} {...rest} />
}

function FittedText({
  minFontSize = 8,
  fitToHeight,
  fitToWidth,
  onFitted,
  ...props
}: WithRequired<Props, 'fitToWidth'>) {
  const scale = useScale()
  const { styleProps, rest } = useScaleProps(props)
  const ref = useRef<HTMLDivElement>(null)
  const [scaledFontSize, setScaledFontSize] = useState<number>()

  useEffect(() => {
    const { children = '', fontSize = 16, fontWeight, lineHeight } = props
    const isFixed = typeof fitToWidth === 'number'
    const fitStyles: CSSProperties = {
      ...(isFixed && { width: widthPx(fitToWidth) }),
      ...(typeof fitToHeight === 'number' && { height: `${fitToHeight}px` }),
      ...(fontWeight && { fontWeight }),
      ...(lineHeight && { lineHeight }),
    }
    let params: Parameters<typeof getFontSize> = [
      children,
      fontSize,
      minFontSize,
      fitToHeight === 'one-line',
      fitStyles,
    ]
    const key = objectHash(params)
    let scaled = cachedFontSizes.get(key)
    if (scaled) return setScaledFontSize(scaled)

    if (isFixed) {
      scaled = getFontSize(...params)
    } else {
      const containerWidth = ref.current?.parentElement?.getBoundingClientRect()?.width ?? 0
      params[4].width = widthPx(containerWidth / scale)
      scaled = getFontSize(...params)
    }

    const { height = 0, width = 0 } = ref.current?.getBoundingClientRect() ?? {}
    onFitted?.({ fontSize: scaled, height: height / scale, width: width / scale })
    cachedFontSizes.set(key, scaled)
    setScaledFontSize(scaled)
  })

  return (
    <Text
      ref={ref}
      // height={typeof fitToHeight === 'number' ? fitToHeight * scale : undefined}
      // {...(fitToHeight && {
      //   overflow: 'hidden',
      //   textOverflow: 'ellipsis',
      //   whiteSpace: fitToHeight === 'one-line' ? 'nowrap' : 'inherit',
      // })}
      {...styleProps}
      {...rest}
      fontSize={(scaledFontSize ?? 16) * scale}
    />
  )
}

const widthPx = (width: number) => `${width - 3}px` // fudge factor

const cachedFontSizes = new Map<string, number>()

function getFontSize(
  text: string | string[],
  maxFontSize: number,
  minFontSize: number,
  oneLine: boolean,
  style: CSSProperties
) {
  const divElement = document.createElement('div')
  const _text = Array.isArray(text) ? text.join(' ') : text
  const textNode = document.createTextNode(_text)
  divElement.appendChild(textNode)
  Object.entries(style).forEach(([key, value]) => {
    // @ts-ignore
    divElement.style[key] = value
  })
  document.body.appendChild(divElement)

  const fitOptions: TextFitOption = {
    minFontSize,
    maxFontSize,
    widthOnly: !style.height,
    multiLine: !oneLine,
  }
  textFit(divElement, fitOptions)

  const innerSpan = divElement.children[0] as HTMLElement
  const scaledFontSize = parseFloat(window.getComputedStyle(innerSpan).fontSize)
  divElement.remove()

  return scaledFontSize
}
